import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { LandingPageStateService } from '../services/landing-page-state.service';
import { NavigationService } from '../services/navigation.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { authCodeFlowConfig } from '../auth-config';
import { LoadingMaskService } from '../services/loading-mask.service';
import { AuthorizationService } from '../services/authorization.service';

const toApplication = (router: Router, applicationId: string | null): UrlTree =>
  router.createUrlTree(['/application'], applicationId ? { queryParams: { id: applicationId } } : {});

const isAuthorizationCallback = (route: ActivatedRouteSnapshot) =>
  route.queryParamMap.has('code') || route.queryParamMap.has('error');

export const AuthRedirectionGuard: CanActivateFn = (route) => {
  const authService = inject(OAuthService);
  authService.configure(authCodeFlowConfig);

  const router = inject(Router);
  const authorizationService = inject(AuthorizationService);
  const navService = inject(NavigationService);
  const stateService = inject(LandingPageStateService);
  const maskService = inject(LoadingMaskService);

  // A plain visit to the root is not a return from ID-porten, so there is nothing to redeem.
  if (!isAuthorizationCallback(route)) {
    return toApplication(router, route.queryParamMap.get('id'));
  }

  // Taken before the redemption so it is consumed exactly once, whichever way this ends.
  const intent = authorizationService.takeLoginIntent();

  maskService.setManualOverride(true);
  maskService.showLoadingMask(null);

  // Called once. Calling tryLogin() a second time re-reads the query string and redeeming the
  // same authorization code twice makes ID-porten answer invalid_grant, which discards the
  // tokens that were just stored.
  return authService.loadDiscoveryDocumentAndTryLogin()
    .then(() => authService.hasValidAccessToken())
    .catch(() => false)
    .then(isLoggedIn => {
      if (intent) {
        stateService.fetchApplicationByID(intent.applicationId);
      }

      if (isLoggedIn && intent) {
        navService.toDetails();
      } else {
        // Never fail silently: without this the user lands back on step 1 with no explanation
        // for why pressing "Neste" appeared to do nothing.
        stateService.reportLoginFailed();
      }
    })
    .finally(() => {
      maskService.setManualOverride(false);
      maskService.turnOffLoadingMask();
    })
    .then(() => toApplication(router, intent?.applicationId ?? route.queryParamMap.get('id')));
};
